import { useCallback, useEffect, useRef } from "react";

import {
  CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
  buildRejectedImageSelectionState,
} from "./catchMediaConstants";
import { revokePreviewOwnedUrl } from "./catchMediaPreview";
import { SUBMIT_PHASES } from "./catchSubmitUtils";

export default function useCatchMediaCleanup({
  mediaState,
  imagePreviewUrl,
  setMediaState,
  setDraftCatchMedia,
  selectionIdRef,
  snapshotInFlightRef,
  setSubmitPhase,
  resetSelectionDerivedConsentState,
  resetSelectionDerivedLocationState,
}) {
  const previewOwnedUrlRef = useRef("");
  const deferredFileInputRef = useRef(null);
  const mediaStateRef = useRef(mediaState);
  const uploadAssetRef = useRef({
    selectionId: 0,
    snapshotStatus: "idle",
    originalFile: null,
    snapshotFile: null,
    previewStatus: "idle",
    previewBlobUrl: "",
    exifStatus: "idle",
  });

  mediaStateRef.current = mediaState;

  useEffect(() => {
    const previousPreviewUrl = previewOwnedUrlRef.current;
    if (previousPreviewUrl && previousPreviewUrl !== imagePreviewUrl) {
      revokePreviewOwnedUrl(previousPreviewUrl);
    }

    previewOwnedUrlRef.current =
      typeof imagePreviewUrl === "string" && imagePreviewUrl.startsWith("blob:")
        ? imagePreviewUrl
        : "";
  }, [imagePreviewUrl]);

  useEffect(() => {
    return () => {
      if (previewOwnedUrlRef.current) {
        revokePreviewOwnedUrl(previewOwnedUrlRef.current);
        previewOwnedUrlRef.current = "";
      }
    };
  }, []);

  useEffect(() => {
    const current = uploadAssetRef.current;
    if (
      current.selectionId === mediaState.selectionId &&
      current.snapshotFile === mediaState.snapshotFile &&
      current.originalFile === mediaState.originalFile
    ) {
      return;
    }

    if (!mediaState.snapshotFile && !mediaState.originalFile) {
      return;
    }

    uploadAssetRef.current = {
      selectionId: mediaState.selectionId,
      snapshotStatus: mediaState.snapshotStatus || "success",
      originalFile: mediaState.originalFile || null,
      snapshotFile: mediaState.snapshotFile,
      previewStatus: mediaState.previewStatus || "idle",
      previewBlobUrl: mediaState.previewUrl || "",
      exifStatus: mediaState.exifStatus || "idle",
    };
  }, [
    mediaState.exifStatus,
    mediaState.originalFile,
    mediaState.previewStatus,
    mediaState.previewUrl,
    mediaState.selectionId,
    mediaState.snapshotFile,
    mediaState.snapshotStatus,
  ]);

  const setMediaStateForSelection = useCallback(
    (nextState, options = {}) => {
      const { persistDraft = true } = options;
      setMediaState(nextState);
      if (persistDraft && typeof setDraftCatchMedia === "function") {
        setDraftCatchMedia(nextState);
      }
    },
    [setDraftCatchMedia, setMediaState]
  );

  const updateMediaState = useCallback(
    (selectionId, updater) => {
      setMediaState((prev) => {
        if (prev.selectionId !== selectionId) {
          return prev;
        }
        const next =
          typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
        if (typeof setDraftCatchMedia === "function") {
          setDraftCatchMedia(next);
        }
        return next;
      });
    },
    [setDraftCatchMedia, setMediaState]
  );

  const clearDeferredFileInput = useCallback((reason = "") => {
    const deferredInput = deferredFileInputRef.current;
    if (!deferredInput) {
      return false;
    }

    const { inputEl, inputId, selectionId, sourceKind } = deferredInput;
    const inputStillWritable = Boolean(inputEl && typeof inputEl.value === "string");
    const inputValueBeforeClear = inputStillWritable ? inputEl.value : "";

    if (inputStillWritable) {
      inputEl.value = "";
    }

    console.log("[CATCH_IMG_DEBUG]", {
      stage: "file_input_cleared_on_terminal_cleanup",
      selectionId: selectionId ?? null,
      inputId: inputId || "",
      sourceKind: sourceKind || "",
      reason: reason || "",
      inputStillWritable,
      hadValueBeforeClear: Boolean(inputValueBeforeClear),
    });

    deferredFileInputRef.current = null;
    return true;
  }, []);

  const finalizeSelectionInputClear = useCallback(
    (inputEl, inputId, selectionId, selectedMediaBundle, options = {}) => {
      if (!inputEl || typeof inputEl.value !== "string") {
        deferredFileInputRef.current = null;
        return;
      }

      const sourceKind = selectedMediaBundle?.snapshotFile
        ? "stable_snapshot"
        : selectedMediaBundle?.originalFile
          ? "original_file"
          : "missing";

      if (sourceKind === "original_file") {
        deferredFileInputRef.current = {
          inputEl,
          inputId,
          selectionId,
          sourceKind,
        };
        console.log("[CATCH_IMG_DEBUG]", {
          stage: "file_input_clear_deferred_for_original_fallback",
          selectionId,
          inputId,
          sourceKind,
          snapshotStatus: selectedMediaBundle?.status || "failed",
          snapshotReadStrategy: selectedMediaBundle?.readStrategy || "",
          reason: options.reason || "selection_ready",
        });
        return;
      }

      deferredFileInputRef.current = null;
      inputEl.value = "";
      console.log("[CATCH_IMG_DEBUG]", {
        stage: "file_input_cleared",
        selectionId,
        inputId,
        clearedAfterSelectionReady: true,
        sourceKind,
        snapshotStatus: selectedMediaBundle?.status || "missing",
        snapshotReadStrategy: selectedMediaBundle?.readStrategy || "",
        reason: options.reason || "selection_ready",
      });
    },
    []
  );

  const resetSelectionDerivedMediaState = useCallback(() => {
    resetSelectionDerivedConsentState?.();
    resetSelectionDerivedLocationState?.();
  }, [resetSelectionDerivedConsentState, resetSelectionDerivedLocationState]);

  const rejectImageSelectionWithoutSnapshot = useCallback(
    (selectionId, options = {}) => {
      const {
        snapshotStatus = "failed",
        failureCode = "",
        failureReason = "",
        message = CATCH_ANDROID_PICKER_UNREADABLE_ERROR_MESSAGE,
        reason = "image_selection_rejected",
      } = options;

      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "image_selection_rejected_without_snapshot",
        selectionId,
        snapshotStatus,
        failureCode,
        failureReason,
        reason,
      });

      uploadAssetRef.current = {
        selectionId,
        snapshotStatus,
        originalFile: null,
        snapshotFile: null,
        previewStatus: "failed",
        previewBlobUrl: "",
        exifStatus: "failed",
      };

      setMediaStateForSelection(
        buildRejectedImageSelectionState(selectionId, message, snapshotStatus)
      );
      setSubmitPhase(SUBMIT_PHASES.IDLE, { reason });

      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "image_selection_requires_repick",
        selectionId,
        snapshotStatus,
        failureCode,
        failureReason,
        reason,
      });
    },
    [setMediaStateForSelection, setSubmitPhase]
  );

  const logSnapshotInFlightUnmount = useCallback(() => {
    const snapshotInFlight = snapshotInFlightRef.current;
    if (!snapshotInFlight) {
      return;
    }

    const { selectionId, startedAt } = snapshotInFlight;
    const ageMs =
      typeof startedAt === "number" ? Math.round(Date.now() - startedAt) : null;
    console.warn("[CATCH_IMG_DEBUG]", {
      stage: "snapshot_inflight_unmount",
      selectionId,
      ageMs,
    });
  }, [snapshotInFlightRef]);

  useEffect(() => {
    return () => {
      clearDeferredFileInput("component_unmount");
      logSnapshotInFlightUnmount();
    };
  }, [clearDeferredFileInput, logSnapshotInFlightUnmount]);

  const clearMediaState = useCallback(() => {
    if (uploadAssetRef.current?.snapshotStatus === "pending") {
      console.warn("[CATCH_IMG_DEBUG]", {
        stage: "snapshot_cleared_while_pending",
        selectionId: uploadAssetRef.current.selectionId ?? null,
      });
    }

    clearDeferredFileInput("clear_media");

    const clearedState = {
      selectionId: selectionIdRef.current,
      selectedFile: null,
      originalFile: null,
      snapshotFile: null,
      snapshotStatus: "idle",
      uploadFile: null,
      uploadFilename: "",
      uploadMimeType: "",
      mediaType: null,
      previewUrl: "",
      previewSource: "",
      previewStatus: "idle",
      exifStatus: "idle",
      submitReady: false,
      blockingError: "",
      blockingErrorCode: "",
      nonBlockingMessage: "",
    };

    uploadAssetRef.current = {
      selectionId: selectionIdRef.current,
      snapshotStatus: "idle",
      originalFile: null,
      snapshotFile: null,
      previewStatus: "idle",
      previewBlobUrl: "",
      exifStatus: "idle",
    };

    setMediaStateForSelection(clearedState);
    setSubmitPhase(SUBMIT_PHASES.IDLE, { reason: "clear_media" });
  }, [clearDeferredFileInput, selectionIdRef, setMediaStateForSelection, setSubmitPhase]);

  return {
    mediaStateRef,
    uploadAssetRef,
    setMediaStateForSelection,
    updateMediaState,
    clearDeferredFileInput,
    finalizeSelectionInputClear,
    resetSelectionDerivedMediaState,
    rejectImageSelectionWithoutSnapshot,
    clearMediaState,
  };
}
